import {
  type IntegrationAddress,
  TCPropertyImportPropertyScope,
  TCPropertyImportSyncMissingPolicy,
} from 'src/api/generated';

export const FRONTEND_MELI_INTEGRATION_MODES = ['PUBLISH', 'SYNC'] as const;

export type FrontendMeliIntegrationMode =
  (typeof FRONTEND_MELI_INTEGRATION_MODES)[number];

export const DEFAULT_FRONTEND_MELI_INTEGRATION_MODE: FrontendMeliIntegrationMode =
  'PUBLISH';
export const DEFAULT_FRONTEND_MELI_AUTO_ASSIGNMENT_ENABLED = false;
export const DEFAULT_FRONTEND_MELI_SYNC_MISSING_POLICY =
  TCPropertyImportSyncMissingPolicy.UPSERT;
export const DEFAULT_FRONTEND_MELI_PROPERTY_SCOPE =
  TCPropertyImportPropertyScope.ACTIVE;

/** Stable identity of the tenant WhatsApp phone published with MeLi items. */
export interface FrontendMeliSellerContactWhatsAppSelection {
  /** Provider account that owns the phone address. */
  integrationProviderId: string;
  /** Provider phone identifier within that account. */
  providerId: string;
}

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const isMeliIntegrationMode = (
  value: unknown
): value is FrontendMeliIntegrationMode =>
  typeof value === 'string' &&
  FRONTEND_MELI_INTEGRATION_MODES.includes(
    value as FrontendMeliIntegrationMode
  );

export const getMeliIntegrationConfigRecord = (
  value: unknown
): Record<string, unknown> => (isObjectRecord(value) ? value : {});

export const getMeliIntegrationMode = (
  config: unknown
): FrontendMeliIntegrationMode => {
  const record = getMeliIntegrationConfigRecord(config);

  return isMeliIntegrationMode(record.mode)
    ? record.mode
    : DEFAULT_FRONTEND_MELI_INTEGRATION_MODE;
};

/** Reads a valid persisted WhatsApp phone selection from MeLi config. */
export const getMeliSellerContactWhatsAppSelection = (
  config: unknown
): FrontendMeliSellerContactWhatsAppSelection | null => {
  const record = getMeliIntegrationConfigRecord(config);
  if (!isObjectRecord(record.sellerContactWhatsApp)) {
    return null;
  }

  const { integrationProviderId, providerId } = record.sellerContactWhatsApp;
  if (
    typeof integrationProviderId !== 'string' ||
    integrationProviderId.length === 0 ||
    typeof providerId !== 'string' ||
    providerId.length === 0
  ) {
    return null;
  }

  return { integrationProviderId, providerId };
};

/** Converts a WhatsApp selection to a collision-safe native select value. */
export const serializeMeliSellerContactWhatsAppSelection = (
  selection: FrontendMeliSellerContactWhatsAppSelection
): string =>
  JSON.stringify([selection.integrationProviderId, selection.providerId]);

/** Returns the stable MeLi selection represented by a connected phone. */
export const getMeliSellerContactWhatsAppSelectionFromPhone = (
  phone: IntegrationAddress
): FrontendMeliSellerContactWhatsAppSelection => ({
  integrationProviderId: phone.integrationProviderId,
  providerId: phone.providerId,
});

/** Compares two nullable WhatsApp phone selections by provider identity. */
export const areMeliSellerContactWhatsAppSelectionsEqual = (
  left: FrontendMeliSellerContactWhatsAppSelection | null,
  right: FrontendMeliSellerContactWhatsAppSelection | null
): boolean =>
  left?.integrationProviderId === right?.integrationProviderId &&
  left?.providerId === right?.providerId;

/**
 * Resolves the form's effective selection. A single connected phone is an
 * implicit backend default and therefore does not require a config write.
 */
export const resolveMeliSellerContactWhatsAppSelection = (
  persisted: FrontendMeliSellerContactWhatsAppSelection | null,
  phones: IntegrationAddress[]
): FrontendMeliSellerContactWhatsAppSelection | null =>
  persisted ??
  (phones.length === 1
    ? getMeliSellerContactWhatsAppSelectionFromPhone(phones[0])
    : null);
